/**
 * Diff Component — HMVMReviewer Design System Phase 1
 * 
 * Display code diffs with added/removed/modified lines
 * 
 * Features:
 * - Visual diff display (added/removed/modified)
 * - Line-by-line or unified diff
 * - Uses semantic theme tokens (independent of product accent)
 * - Square geometry (border-radius: 0)
 */

import { HTMLAttributes } from 'react';

export type DiffLineType = 'added' | 'removed' | 'modified' | 'unchanged';

export interface DiffLine {
  type: DiffLineType;
  lineNumber?: number;
  content: string;
}

export interface DiffProps extends HTMLAttributes<HTMLDivElement> {
  lines: DiffLine[];
  filename?: string;
  showLineNumbers?: boolean;
}

const lineTypeStyles: Record<DiffLineType, { bg: string; border: string; symbol: string }> = {
  added: {
    bg: 'bg-[var(--reviewer-diff-added-bg)]',
    border: 'border-l-2 border-[var(--reviewer-diff-added-border)]',
    symbol: '+',
  },
  removed: {
    bg: 'bg-[var(--reviewer-diff-removed-bg)]',
    border: 'border-l-2 border-[var(--reviewer-diff-removed-border)]',
    symbol: '-',
  },
  modified: {
    bg: 'bg-[var(--reviewer-diff-modified-bg)]',
    border: 'border-l-2 border-[var(--reviewer-diff-modified-border)]',
    symbol: '~',
  },
  unchanged: {
    bg: '',
    border: 'border-l-2 border-transparent',
    symbol: ' ',
  },
};

export function Diff({
  lines,
  filename,
  showLineNumbers = true,
  className = '',
  ...props
}: DiffProps) {
  return (
    <div
      className={`
        bg-[var(--reviewer-code-bg)]
        border border-[var(--reviewer-code-border)]
        ${className}
      `}
      {...props}
    >
      {/* Header */}
      {filename && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--reviewer-code-border)]">
          <span className="text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)] font-mono">
            {filename}
          </span>
          <div className="flex items-center gap-4 text-[var(--reviewer-text-xs)] font-mono">
            <span className="text-[var(--reviewer-diff-added)]">
              + {lines.filter(l => l.type === 'added').length}
            </span>
            <span className="text-[var(--reviewer-diff-removed)]">
              - {lines.filter(l => l.type === 'removed').length}
            </span>
          </div>
        </div>
      )}
      
      {/* Diff Content */}
      <div className="overflow-x-auto">
        <pre className="m-0">
          <code className="font-mono text-[var(--reviewer-text-sm)]">
            {lines.map((line, index) => {
              const styles = lineTypeStyles[line.type];
              return (
                <div
                  key={index}
                  className={`flex ${styles.bg} ${styles.border} pl-2`}
                >
                  <span className="inline-block w-4 text-center text-[var(--reviewer-text-tertiary)] select-none">
                    {styles.symbol}
                  </span>
                  {showLineNumbers && line.lineNumber !== undefined && (
                    <span className="inline-block w-12 text-right pr-4 text-[var(--reviewer-text-tertiary)] select-none">
                      {line.lineNumber}
                    </span>
                  )}
                  <span className="flex-1 pr-4">{line.content}</span>
                </div>
              );
            })}
          </code>
        </pre>
      </div>
    </div>
  );
}
